import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {DateTimePicker} from "@mui/x-date-pickers";
import * as moment from 'moment-timezone';
import React, {useEffect, useState} from "react";
import {useMutation, useQuery} from "@apollo/client";

import {
    ALL_SERVICE_TYPES,
    CREATE_SHIFT_PLANS,
    GET_DETAILED_SHIFT_PLANS,
    GET_SHIFT_PLANS
} from "../../queries/GraphQLQueries";
import CustomAlert from "../CustomAlert";
import {DATE_FORMAT_IN_READABLE_FORMAT, DATE_WITH_TIME_FORMAT} from "../../config/DateTimeConstants";
import {
    REPEAT_FREQUENCY,
    ROUNDING_MINUTES_FOR_SHIFT_END_TIME,
    SECONDS_INTO_MINUTES,
    MOMENT_UNIT_MINUTES,
    WARNING_UPDATE_DELAY_IN_MILISECONDS,
    MAXIMUM_NUMBER_OF_MONTHS_ALLOWED_FOR_CREATING_REPEATING_PLANS
} from "../../domain/ShiftPlanConstants";
import {isTimeInvalid, isSameServiceTypesNotOverlapping} from "../../helpers/ShiftHelper";
import ServiceTypeMultiSelect from "../common/ServiceTypeMultiSelect";
import ShiftTimeSelection from "../schedule/ShiftTimeSelection";

const styles = {
    dialog: {
        '& .MuiDialog-paper': {
            width: '50rem',
            maxWidth: 'auto',
        },
    },
    warningContainer: {
        marginTop: '0.5rem'
    },
    warningText: {
        fontWeight: 'bold'
    },
    checkInContainer: {
        marginLeft: '2rem'
    },
    repeatFrequencyFormControl: {
        width: '15rem',
        m: '1rem'
    },
    repeatDatePickerStack: {
        p: '1rem'
    },
    repeatDatePicker: {
        width: '48%'
    },
    addButton: {
        marginBottom: '0.5rem',
        marginRight: '0.9rem'
    }
};

export default function AddShiftPlanDialog({openAddShiftPlanDialog, handleClose, onShiftPlanAdded, currentDate}) {

    const DEFAULT_SHIFT_PLAN_START_TIME = currentDate;
    const DEFAULT_SHIFT_PLAN_END_TIME = moment(currentDate).add(ROUNDING_MINUTES_FOR_SHIFT_END_TIME, MOMENT_UNIT_MINUTES);

    const serviceTypeSelectionOptions = {
        enableBookableServices: false,
        enableServiceTypeDetails: true
    };

    const [shiftPlanStartTime, setShiftPlanStartTime] = useState(DEFAULT_SHIFT_PLAN_START_TIME);
    const [shiftPlanEndTime, setShiftPlanEndTime] = useState(DEFAULT_SHIFT_PLAN_END_TIME);
    const [canAddShiftPlan, setCanAddShiftPlan] = useState(false);
    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [selectedRepeatFrequency, setSelectedRepeatFrequency] = useState(REPEAT_FREQUENCY.WEEKDAYS);
    const [repeatFrom, setRepeatFrom] = useState(moment(currentDate).startOf('month'));
    const [repeatUntil, setRepeatUntil] = useState(moment(currentDate).endOf('month'));
    const [disableRepeatItems, setDisableRepeatItems] = useState(true);
    const [includeCheckInTimeslot, setIncludeCheckInTimeslot] = useState(true);
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
    const [bookableServiceTypesTimestamps, setBookableServiceTypesTimestamps] = useState({});
    const [showWarning, setShowWarning] = useState(false);

    const handleCloseCustomAlert = () => setAlertMessage(null);
    const handleBookableServiceTypesTimestampUpdate = (updatedTimeStamps) => setBookableServiceTypesTimestamps(updatedTimeStamps);

    const {data: serviceData} = useQuery(ALL_SERVICE_TYPES);
    const [createShiftPlan, {loading: isCreatingShiftPlans}] = useMutation(CREATE_SHIFT_PLANS, {
        refetchQueries: [
            {query: GET_DETAILED_SHIFT_PLANS},
            {query: GET_SHIFT_PLANS},
        ]
    });

    useEffect(() => {
        if (!openAddShiftPlanDialog) {
            resetDialog();
        }
    }, [openAddShiftPlanDialog]);

    useEffect(() => {
        setShiftPlanStartTime(currentDate);
        setShiftPlanEndTime(DEFAULT_SHIFT_PLAN_END_TIME);
        setRepeatFrom(moment(currentDate).startOf('month'));
        setRepeatUntil(moment(currentDate).endOf('month'));
        setSelectedRepeatFrequency(REPEAT_FREQUENCY.WEEKDAYS);
        setDisableRepeatItems(true);
        setSelectedServiceTypes([]);
        setBookableServiceTypesTimestamps({});
    }, [currentDate]);

    useEffect(() => {
        const isSelectedShiftPlanTimeValid =
            shiftPlanStartTime?.toString().trim() !== ''
            && shiftPlanEndTime?.toString().trim() !== ''
            && moment(shiftPlanStartTime).isValid()
            && moment(shiftPlanEndTime).isValid()
            && !isTimeInvalid(bookableServiceTypesTimestamps, shiftPlanStartTime, shiftPlanEndTime)
            && isSameServiceTypesNotOverlapping(bookableServiceTypesTimestamps)
            && selectedServiceTypes.length > 0;

        let areRepeatDatesValid = true;

        if (!disableRepeatItems) {
            const allowableRepeatUntil = moment(repeatFrom).add(MAXIMUM_NUMBER_OF_MONTHS_ALLOWED_FOR_CREATING_REPEATING_PLANS, 'months');

            areRepeatDatesValid = repeatFrom.isSameOrBefore(repeatUntil)
                && repeatUntil.isSameOrBefore(allowableRepeatUntil);
        }

        setCanAddShiftPlan(isSelectedShiftPlanTimeValid && areRepeatDatesValid);
    }, [shiftPlanStartTime, shiftPlanEndTime, disableRepeatItems, repeatUntil, repeatFrom, selectedServiceTypes, bookableServiceTypesTimestamps]);

    useEffect(() => {
        initializeDefaultStateValues();

        if (!serviceData?.serviceTypes) {
            return;
        }

        setAllServiceTypes(serviceData.serviceTypes);
    }, [serviceData]);

    const initializeDefaultStateValues = () => {
        setShiftPlanStartTime(DEFAULT_SHIFT_PLAN_START_TIME);
        setShiftPlanEndTime(DEFAULT_SHIFT_PLAN_END_TIME);
    };

    useEffect(() => {
        if (selectedServiceTypes.length === 0) {
            return;
        }

        const updatedServiceTimes = {...bookableServiceTypesTimestamps};

        selectedServiceTypes.forEach(serviceType => {
            const existingServiceTypeTime = updatedServiceTimes[serviceType.uniqueBookableServiceRowId];
            const isServiceTypeTimeInPast = moment(existingServiceTypeTime?.startTime).isBefore(moment(shiftPlanStartTime));
            const isServiceTypeTimeInFuture = moment(existingServiceTypeTime?.endTime).isAfter(moment(shiftPlanEndTime));

            if (!existingServiceTypeTime || isServiceTypeTimeInPast || isServiceTypeTimeInFuture) {

                const maxDurationInMinutes = (serviceType.timeslotLengthInSeconds * serviceType.maxPeriodsInShift) / SECONDS_INTO_MINUTES;
                const maxEndTime = moment(shiftPlanStartTime).add(maxDurationInMinutes, MOMENT_UNIT_MINUTES);
                let adjustedEndTime = moment(shiftPlanEndTime);

                if (adjustedEndTime.isAfter(maxEndTime)) {
                    adjustedEndTime = maxEndTime;
                }

                const startTime = isServiceTypeTimeInPast || adjustedEndTime.isSameOrBefore(existingServiceTypeTime?.startTime) || !existingServiceTypeTime
                    ? moment(shiftPlanStartTime)
                    : existingServiceTypeTime.startTime;

                updatedServiceTimes[serviceType.uniqueBookableServiceRowId] = {
                    startTime,
                    endTime: adjustedEndTime
                };
            }
        });

        setBookableServiceTypesTimestamps(updatedServiceTimes);
    }, [shiftPlanStartTime, shiftPlanEndTime, selectedServiceTypes]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedServiceTypes.length === 0) {
                setShowWarning(false);
            } else {
                setShowWarning(
                    isTimeInvalid(bookableServiceTypesTimestamps, shiftPlanStartTime, shiftPlanEndTime)||
                    !isSameServiceTypesNotOverlapping(bookableServiceTypesTimestamps)
                );
            }
        }, WARNING_UPDATE_DELAY_IN_MILISECONDS);

        return () => clearTimeout(timer);
    }, [selectedServiceTypes, bookableServiceTypesTimestamps, shiftPlanStartTime, shiftPlanEndTime]);

    const resetDialog = () => {
        setShiftPlanStartTime(currentDate);
        setShiftPlanEndTime(DEFAULT_SHIFT_PLAN_END_TIME);
        setRepeatFrom(moment(currentDate).startOf('month'));
        setRepeatUntil(moment(currentDate).endOf('month'));
        setSelectedRepeatFrequency(REPEAT_FREQUENCY.WEEKDAYS);
        setDisableRepeatItems(true);
        setIncludeCheckInTimeslot(true);
        setSelectedServiceTypes([]);
        setBookableServiceTypesTimestamps({});
    }

    const handleCheckInTimeSlotChange = (event) => {
        setIncludeCheckInTimeslot(event.target.checked);
    };

    const handleRepeatToggleChanged = (event) => setDisableRepeatItems(!event.target.checked);

    const getRepeatFrequencyKey = (repeatFrequencyValue) => {
        const key = Object.keys(REPEAT_FREQUENCY).find(key => REPEAT_FREQUENCY[key] === repeatFrequencyValue);
        return key || null;
    };

    const handleRepeatFrequencyChanged = (event) => setSelectedRepeatFrequency(event.target.value);

    const handleAddShiftPlan = () => {
        const repeatFrequencyValue = disableRepeatItems ? null : getRepeatFrequencyKey(selectedRepeatFrequency);
        const repeatFromValue = disableRepeatItems ? null : moment(repeatFrom).format(DATE_WITH_TIME_FORMAT);
        const repeatUntilValue = disableRepeatItems ? null : moment(repeatUntil).format(DATE_WITH_TIME_FORMAT);

        createShiftPlan(
            {
                variables: {
                    createBookableServiceTypePlansInput: buildBookableServiceTypesPlanInput(),
                    startTime: moment(shiftPlanStartTime).format(DATE_WITH_TIME_FORMAT),
                    endTime: moment(shiftPlanEndTime).format(DATE_WITH_TIME_FORMAT),
                    includeCheckInTimeslot: includeCheckInTimeslot,
                    repeatFrequency: repeatFrequencyValue,
                    repeatFrom: repeatFromValue,
                    repeatUntil: repeatUntilValue
                }
            }).then(() => {
            onShiftPlanAdded();
        }).catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            setCanAddShiftPlan(true);
            console.log(error);
        });
    }

    const buildBookableServiceTypesPlanInput = () => selectedServiceTypes.map(serviceType => {

        const uniqueBookableServiceRowId = serviceType.uniqueBookableServiceRowId;
        const serviceTypeKeycode = serviceType.keycode;

        const serviceTypesTimestamp = bookableServiceTypesTimestamps[uniqueBookableServiceRowId] || {};
        const {id, startTime, endTime} = serviceTypesTimestamp;

        return {
            serviceTypeKeycode,
            id,
            startTime: moment(startTime).format(DATE_WITH_TIME_FORMAT),
            endTime: moment(endTime).format(DATE_WITH_TIME_FORMAT)
        }
    });

    return <>
        <Dialog
            open={openAddShiftPlanDialog}
            maxWidth={false}
            onClose={handleClose}
            disableRestoreFocus
            sx={styles.dialog}>
            <DialogTitle>Add Shift Plan</DialogTitle>
            <DialogContent>
                <Grid container spacing={1}>
                    <ShiftTimeSelection
                        shiftStartTime={shiftPlanStartTime}
                        setShiftStartTime={setShiftPlanStartTime}
                        shiftEndTime={shiftPlanEndTime}
                        setShiftEndTime={setShiftPlanEndTime}
                    />
                    <Grid container spacing={0}>
                        <Grid size={12}>
                            <Typography variant="h6">Shift Bookable Services</Typography>
                        </Grid>
                        <Grid size={12}>
                            <ServiceTypeMultiSelect
                                selectedServiceTypes={selectedServiceTypes}
                                setSelectedServiceTypes={setSelectedServiceTypes}
                                availableServiceTypes={allServiceTypes}
                                serviceTypeSelectionOptions={serviceTypeSelectionOptions}
                                handleBookableServiceTypesTimestampUpdate={handleBookableServiceTypesTimestampUpdate}
                                bookableServiceTypesTimestamps={bookableServiceTypesTimestamps}
                                startTime={shiftPlanStartTime}
                                endTime={shiftPlanEndTime}/>
                        </Grid>
                    </Grid>
                    <Grid size={12} sx={styles.warningContainer}>
                        {showWarning && (
                            <Typography color="error" sx={styles.warningText}>
                                Warning: The shift and bookable service times don't match up.
                            </Typography>
                        )}
                    </Grid>
                    <Grid size={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">Repeat</Typography>
                            <Switch onChange={handleRepeatToggleChanged} />
                        </Stack>
                    </Grid>
                    <Grid size={7} sx={styles.checkInContainer}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">Require Performer Check-in</Typography>
                            <Switch checked={includeCheckInTimeslot} onChange={handleCheckInTimeSlotChange} />
                        </Stack>
                    </Grid>

                    <Grid size={12}>
                        <Paper elevation={4}>
                            <FormControl disabled={disableRepeatItems} sx={styles.repeatFrequencyFormControl}>
                                <InputLabel>Repeat Frequency</InputLabel>
                                <Select
                                    value={selectedRepeatFrequency}
                                    onChange={handleRepeatFrequencyChanged}
                                    label="Repeat Frequency"
                                    MenuProps={{MenuListProps: {sx: {paddingBottom: 0}}}}>
                                    {Object.entries(REPEAT_FREQUENCY).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{value}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Stack spacing={2} sx={styles.repeatDatePickerStack} alignItems="center" justifyContent="center" direction="row">
                                <DateTimePicker
                                    label={'Repeat From'}
                                    value={repeatFrom}
                                    onChange={newValue => setRepeatFrom(newValue)}
                                    ampm={false}
                                    renderInput={params => <TextField {...params} fullWidth />}
                                    disableMaskedInput={true}
                                    sx={styles.repeatDatePicker}
                                    format={DATE_FORMAT_IN_READABLE_FORMAT}
                                    disabled={disableRepeatItems} />
                                <DateTimePicker
                                    label={'Repeat Until'}
                                    value={repeatUntil}
                                    ampm={false}
                                    renderInput={params => <TextField {...params} fullWidth />}
                                    onChange={newValue => setRepeatUntil(newValue)}
                                    disableMaskedInput={true}
                                        sx={styles.repeatDatePicker}
                                    format={DATE_FORMAT_IN_READABLE_FORMAT}
                                    disabled={disableRepeatItems} />
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={styles.addButton}
                    variant="contained"
                    color="primary"
                    onClick={handleAddShiftPlan}
                    disabled={!canAddShiftPlan || isCreatingShiftPlans}>
                    Add
                </Button>
            </DialogActions>
            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
        </Dialog>
    </>
};